<p-toast></p-toast>
<app-page-shell>
    <app-page-hero eyebrow="Delivery Schedule"
        [title]="isNew() ? 'Add Delivery Schedule' : 'Edit Delivery Schedule'"
        [subtitle]="(project()?.projectNumber || '') + ' | ' + (project()?.projectName || '')">
        <div actions class="page-actions">
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="cancel()"></p-button>
            <p-button [label]="isNew() ? 'Create Schedule' : 'Save Changes'" icon="pi pi-check"
                [loading]="saving()" [disabled]="loading() || !project()" (click)="save()"></p-button>
        </div>
    </app-page-hero>

    @if (loading()) {
        <div class="loading-state">Loading schedule form...</div>
    } @else {
        <div class="form-stack">
            <app-page-form-section icon="pi pi-map-marker" title="Destination and Item"
                subtitle="Choose any location and Warehousing item from master data for this delivery.">
                <div class="form-grid">
                    <div class="field-with-action">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select appLazyLocationPicker [appLazyLocationPicker]="companyId"
                                    optionLabel="name" optionValue="id" [filter]="true"
                                    filterBy="name,externalLocationReference,shortDescription,longDescription"
                                    [ngModel]="schedule().locationId" (ngModelChange)="onStoreChange($event)"
                                    appendTo="body" styleClass="w-full"></p-select>
                                <label>Target Store / Location *</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (canCreateLocation) {
                            <p-button icon="pi pi-plus" label="New Location" severity="secondary" [outlined]="true"
                                (click)="showLocationForm.set(true)"></p-button>
                        }
                    </div>
                    <div class="field-with-action">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select [options]="itemOptions()" optionLabel="label" optionValue="value"
                                    [filter]="true" [disabled]="quantityLocked()" [ngModel]="selectedItemValue()"
                                    (ngModelChange)="onItemChange($event)" appendTo="body" styleClass="w-full"></p-select>
                                <label>Warehousing Stand / Item *</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (canCreateItem) {
                            <p-button icon="pi pi-plus" label="New Item" severity="secondary" [outlined]="true"
                                [disabled]="quantityLocked()" (click)="createItem()"></p-button>
                        }
                    </div>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-inputNumber [ngModel]="schedule().standQty" (ngModelChange)="updateField('standQty', $event)"
                                [min]="0.0001" [minFractionDigits]="0" [maxFractionDigits]="4"
                                [disabled]="quantityLocked()" [useGrouping]="false" styleClass="w-full"></p-inputNumber>
                            <label>Stand Qty *</label>
                        </p-iftalabel>
                    </p-inputGroup>
                    <div class="quantity-context" aria-live="polite">
                        @if (quantityLocked()) {
                            <strong>Quantity locked</strong>
                            <span>An execution task or transport record already uses this quantity.</span>
                            @if (canCorrectQuantity) {
                                <p-button label="Correct Quantity" icon="pi pi-pencil" severity="secondary"
                                    [outlined]="true" [loading]="correctionLoading" (click)="openQuantityCorrection()"></p-button>
                            }
                        }
                        @if (quantitySummary()) {
                            @let summary = quantitySummary()!;
                            <span>Project item: <strong>{{ summary.itemQuantity }}</strong></span>
                            <span>Scheduled after save: <strong>{{ projectedQuantity() ?? summary.scheduledQuantity }}</strong></span>
                            @if (quantityMismatch()) {
                                <small class="quantity-warning">The schedule total will differ from the project item quantity. Review the other schedules before saving.</small>
                            }
                        }
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="pi pi-users" title="Supplier and Contact"
                subtitle="Optional row-level supplier and delivery contact overrides.">
                <div class="form-grid">
                    <div class="field-with-action">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select [options]="supplierOptions()" optionLabel="label" optionValue="value"
                                    [filter]="true" [showClear]="true" [ngModel]="schedule().supplierClientId"
                                    (ngModelChange)="updateField('supplierClientId', $event)"
                                    appendTo="body" styleClass="w-full"></p-select>
                                <label>CRM Supplier</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        <p-button icon="pi pi-plus" label="New Supplier" severity="secondary" [outlined]="true"
                            (click)="createSupplier()"></p-button>
                    </div>
                    <div class="field-with-action">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select [options]="contactOptions()" optionLabel="label" optionValue="value"
                                    [filter]="true" [showClear]="true" [ngModel]="selectedContactId()"
                                    (ngModelChange)="onContactChange($event)" appendTo="body" styleClass="w-full"></p-select>
                                <label>Contact Override</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        <p-button icon="pi pi-plus" label="New Contact" severity="secondary" [outlined]="true"
                            (click)="createContact()"></p-button>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="pi pi-calendar" title="Schedule and Return Plan"
                subtitle="Dates and return routing override this delivery row only.">
                <div class="form-grid">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-datePicker [ngModel]="schedule().plannedInstallationDateOverride"
                                (ngModelChange)="updateField('plannedInstallationDateOverride', $event)"
                                [showIcon]="true" appendTo="body" styleClass="w-full"></p-datePicker>
                            <label>Installation Date Override</label>
                        </p-iftalabel>
                    </p-inputGroup>
                    @if (supportsRemovalFlow()) {
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-calendar-minus"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker [ngModel]="schedule().plannedRemovalDateOverride"
                                    (ngModelChange)="updateField('plannedRemovalDateOverride', $event)"
                                    [showIcon]="true" [disabled]="removalOutcomeCompleted()"
                                    appendTo="body" styleClass="w-full"></p-datePicker>
                                <label>Removal Date Override</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-share-alt"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select [options]="destinationOptions" optionLabel="label" optionValue="value"
                                    [showClear]="true" [ngModel]="schedule().returnDestinationType"
                                    (ngModelChange)="onDestinationChange($event)" [disabled]="removalOutcomeCompleted()"
                                    appendTo="body" styleClass="w-full"></p-select>
                                <label>Return Destination Type</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-replay"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select [options]="dispositionOptions()" optionLabel="label" optionValue="value"
                                    [showClear]="true" [ngModel]="schedule().returnDisposition"
                                    (ngModelChange)="onDispositionChange($event)"
                                    [disabled]="removalOutcomeCompleted() || !schedule().returnDestinationType"
                                    appendTo="body" styleClass="w-full"></p-select>
                                <label>Return Disposition</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (schedule().returnDestinationType === 'RETURN_WAREHOUSE') {
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select [options]="warehouseOptions()" optionLabel="label" optionValue="value"
                                        [filter]="true" [showClear]="true" [ngModel]="schedule().returnWarehouseId"
                                        (ngModelChange)="updateField('returnWarehouseId', $event)"
                                        [disabled]="removalOutcomeCompleted()" appendTo="body" styleClass="w-full"></p-select>
                                    <label>Return Warehouse</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        }
                        @if (schedule().returnDestinationType === 'RETURN_LOCATION') {
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select appLazyLocationPicker [appLazyLocationPicker]="companyId"
                                        optionLabel="name" optionValue="id" [filter]="true"
                                        filterBy="name,externalLocationReference,shortDescription,longDescription"
                                        [showClear]="true" [ngModel]="schedule().returnLocationId"
                                        (ngModelChange)="updateField('returnLocationId', $event)"
                                        [disabled]="removalOutcomeCompleted()" appendTo="body" styleClass="w-full"></p-select>
                                    <label>Return Location</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        }
                        @if (requiresReturnCondition()) {
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-check-circle"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select [options]="conditionOptions()" optionLabel="label" optionValue="value"
                                        [filter]="true" [showClear]="true" [ngModel]="schedule().returnConditionId"
                                        (ngModelChange)="updateField('returnConditionId', $event)"
                                        [disabled]="removalOutcomeCompleted()" appendTo="body" styleClass="w-full"></p-select>
                                    <label>Return Condition</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        }
                    }
                    <p-inputGroup class="full-width">
                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <textarea pTextarea rows="4" [ngModel]="schedule().comment"
                                (ngModelChange)="updateField('comment', $event)" class="w-full"></textarea>
                            <label>Delivery Comment</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
            </app-page-form-section>

            @if (locationReuseCandidates().length) {
                <app-page-form-section icon="pi pi-map" title="Possible Existing Locations"
                    subtitle="Review similar locations before creating a new one.">
                    <div class="candidate-list">
                        @for (candidate of locationReuseCandidates(); track candidate.id) {
                            <div class="candidate-row">
                                <div><strong>{{ candidate.label }}</strong><small>Matched by {{ candidate.matchReason }}</small></div>
                                <p-button label="Use This Location" icon="pi pi-check" severity="secondary" [outlined]="true"
                                    [loading]="applyingLocationSuggestionId() === candidate.id"
                                    [disabled]="applyingLocationSuggestionId() !== null"
                                    (click)="applySuggestedLocation(candidate.id)"></p-button>
                            </div>
                        }
                    </div>
                </app-page-form-section>
            }
            <div class="footer-actions">
                <p-button label="Cancel" severity="secondary" [outlined]="true" (click)="cancel()"></p-button>
                <p-button [label]="isNew() ? 'Create Schedule' : 'Save Changes'" icon="pi pi-check"
                    [loading]="saving()" (click)="save()"></p-button>
            </div>
        </div>
    }
    <app-customer-form [displayModal]="showCustomerForm()" dialogHeader="Create Delivery Contact"
        submitButtonLabel="Create" [company]="selectedCompany()" [customer]="selectedCustomer()"
        (modalClosed)="onCustomerFormClosed($event)"></app-customer-form>
    <app-party-quick-create-dialog [visible]="showLocationForm()"
        (visibleChange)="showLocationForm.set($event)" mode="location" [countries]="countries()"
        [owningCompany]="selectedCompany()" [defaultCountryId]="selectedCompany().countryId || null"
        (created)="onLocationCreated($event)"></app-party-quick-create-dialog>
    <p-dialog header="Correct Schedule Quantity" [(visible)]="correctionVisible" [modal]="true"
        [style]="{width: 'min(1080px, 94vw)'}" [breakpoints]="{'860px': '96vw'}"
        [draggable]="false" [resizable]="false" styleClass="master-create-dialog quantity-correction-dialog">
        @if (correctionPreview) {
            <section class="master-dialog-body">
                <aside class="master-dialog-summary">
                    <div class="summary-icon"><i class="pi pi-refresh"></i></div>
                    <div>
                        <small>Delivery schedule</small>
                        <h3>Replace the quantity safely</h3>
                        <p>Confirming cancels linked release families and warehouse work, changes the quantity, and creates replacement releases. If any step fails, nothing is saved.</p>
                    </div>
                    <div class="master-dialog-facts">
                        <div><span>Current quantity</span><strong>{{ correctionPreview.quantity }}</strong></div>
                        <div><span>Release families</span><strong>{{ correctionPreview.releaseImpact.length }}</strong></div>
                    </div>
                    @if (correctionPreview.installationTaskId || correctionPreview.removalTaskId) {
                        <p class="task-note">Unstarted installation task #{{ correctionPreview.installationTaskId || '-' }} and removal task
                            #{{ correctionPreview.removalTaskId || '-' }} keep their IDs; item quantities are refreshed.</p>
                    }
                </aside>

                <div class="master-dialog-form">
                    <section class="correction-section">
                        <div class="section-heading">
                            <small>01 · Impact</small>
                            <h4>Work to cancel</h4>
                        </div>
                        <div class="impact-list">
                            @for (impact of correctionPreview.releaseImpact; track impact.rootReleaseId) {
                                <div class="impact-row">
                                    <strong>Release #{{ impact.rootReleaseId }}</strong>
                                    @for (route of correctionPreview.releaseRoutes; track route.releaseId) {
                                        @if (route.releaseId === impact.rootReleaseId) {
                                            <small>{{ route.originName || ('Location #' + route.originLocationId) }} →
                                                {{ route.destinationName || ('Location #' + route.destinationLocationId) }}</small>
                                        }
                                    }
                                    <small>{{ impact.releaseIds.length }} release leg(s),
                                        {{ impact.transferIds.length }} transfer(s), {{ impact.dispatchIds.length }} dispatch(es)</small>
                                </div>
                            } @empty {
                                <p class="section-help">No linked releases or warehouse documents.</p>
                            }
                        </div>
                    </section>

                    <section class="correction-section">
                        <div class="section-heading">
                            <small>02 · Correction</small>
                            <h4>New quantity and reason</h4>
                        </div>
                        <div class="correction-grid">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-inputNumber inputId="correctionQuantity" [(ngModel)]="correctionQuantity" [min]="0.0001" [maxFractionDigits]="4"
                                        [useGrouping]="false" styleClass="w-full"></p-inputNumber>
                                    <label for="correctionQuantity">New quantity *</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            <p-inputGroup class="master-wide-field">
                                <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <textarea id="correctionReason" pTextarea [(ngModel)]="correctionReason" rows="2" maxlength="500" class="w-full"></textarea>
                                    <label for="correctionReason">Reason for cancellation *</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </div>
                    </section>

                    <section class="correction-section">
                        <div class="section-heading">
                            <small>03 · Stock</small>
                            <h4>Replacement stock allocations</h4>
                            <p>Reduce existing allocations here. Add stock from available reserved sources for an increase.</p>
                        </div>
                        <div class="allocation-list">
                            @for (row of correctionExisting; track row.allocationId) {
                                <div class="allocation-row">
                                    <span>{{ row.label || ('Allocation #' + row.allocationId) }}</span>
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <p-inputNumber [inputId]="'existing-allocation-' + row.allocationId" [(ngModel)]="row.quantity" [min]="0" [max]="row.maximum" [maxFractionDigits]="4"
                                                [useGrouping]="false" styleClass="w-full"></p-inputNumber>
                                            <label [for]="'existing-allocation-' + row.allocationId">Replacement quantity</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                            }
                            @for (row of correctionAvailable; track $index; let index = $index) {
                                <div class="allocation-row">
                                    <span>{{ row.label }}</span>
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
                                        <p-iftalabel>
                                            <p-inputNumber [inputId]="'available-allocation-' + index" [(ngModel)]="row.quantity" [min]="0"
                                                [max]="row.candidate.availableToAllocate || 0" [maxFractionDigits]="4"
                                                [useGrouping]="false" styleClass="w-full"></p-inputNumber>
                                            <label [for]="'available-allocation-' + index">Additional quantity</label>
                                        </p-iftalabel>
                                    </p-inputGroup>
                                </div>
                            }
                        </div>
                    </section>

                    @if (correctionPreview.installation) {
                        <section class="correction-section">
                            <div class="section-heading"><small>04 · Route</small><h4>Installation route</h4></div>
                            <div class="correction-grid">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                    <p-iftalabel><p-datepicker inputId="installationPickup" [(ngModel)]="correctionPreview.installation.pickupWindowStart"
                                        [showTime]="true" appendTo="body" styleClass="w-full"></p-datepicker>
                                        <label for="installationPickup">Pickup from</label></p-iftalabel></p-inputGroup>
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                    <p-iftalabel><p-datepicker inputId="installationDelivery" [(ngModel)]="correctionPreview.installation.deliveryWindowEnd"
                                        [showTime]="true" appendTo="body" styleClass="w-full"></p-datepicker>
                                        <label for="installationDelivery">Delivery by</label></p-iftalabel></p-inputGroup>
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                                    <p-iftalabel><p-inputNumber inputId="installationRouteTemplate" [(ngModel)]="correctionPreview.installation.routeTemplateId"
                                        [useGrouping]="false" styleClass="w-full"></p-inputNumber>
                                        <label for="installationRouteTemplate">Route template ID</label></p-iftalabel></p-inputGroup>
                            </div>
                        </section>
                    }
                    @if (correctionPreview.removal) {
                        <section class="correction-section">
                            <div class="section-heading"><small>05 · Route</small><h4>Removal route</h4></div>
                            <div class="correction-grid">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                    <p-iftalabel><p-datepicker inputId="removalPickup" [(ngModel)]="correctionPreview.removal.pickupWindowStart"
                                        [showTime]="true" appendTo="body" styleClass="w-full"></p-datepicker>
                                        <label for="removalPickup">Removal pickup from</label></p-iftalabel></p-inputGroup>
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                    <p-iftalabel><p-datepicker inputId="removalDelivery" [(ngModel)]="correctionPreview.removal.deliveryWindowEnd"
                                        [showTime]="true" appendTo="body" styleClass="w-full"></p-datepicker>
                                        <label for="removalDelivery">Removal delivery by</label></p-iftalabel></p-inputGroup>
                            </div>
                        </section>
                    }
                </div>
            </section>
        }
        <ng-template pTemplate="footer">
            <div class="master-dialog-footer">
                <span class="footer-spacer"></span>
                <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="correctionVisible = false"></p-button>
                <p-button label="Cancel and Replace" icon="pi pi-check" [loading]="correctionLoading"
                    (click)="submitQuantityCorrection()"></p-button>
            </div>
        </ng-template>
    </p-dialog>
</app-page-shell>
